import React, { memo, lazy, useEffect, useMemo } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import RouteElement from '@RootCommon/components/RouteElement'
import { retryFailedRequests, upgradeLog, enterWebSiteLog, leaveWebSiteLog, saveLeaveWebSiteLog } from '@RootCommon/utils/behaviorLog'
import { getIsOrderManager, getLoginShopid, getRegionByCookie, isSingleAcct, getInWindowsApp } from '@RootCommon/utils'
import Icon from '@roo/roo/Icon'
import { useTranslation } from '@sailor/i18n-web'
import '@/common/assets/styles/index.less'
import IconLoading from '@RootCommon/components/IconLoading'
import { accountDetailInfoAtom, imEntryShowAtom, shopInfoFinishedAtom } from '@/common/recoil/atom'
import useAccountInfo from '@/common/hooks/useAccountInfo'
import useAuthInfo from '@/common/hooks/useAuthInfo'
import useMsgInfo from '@/common/hooks/useMsgInfo'
import { getI18nWebParamsPApi, getShowImMsgBoxPApi } from '@/common/api/prefetchApis'
import useShopInfo from '@/common/hooks/useShopInfo'
import { initOneIdWeb } from '@/common/utils/uuid'
import { autoUploadLogs } from '@/common/utils/omReports/omLogReport'
import { CommonNotifacation } from '@/components/CommonNotifacation'
import useHeartbeatReport from './common/hooks/useHeartbeatReport'

// @ts-ignore
Icon.extend({ scriptUrl: [SAILOR_B_ICON_URL_903, SAILOR_B_PC_ICON_URL_899, SAILOR_B_PC_ICON_URL_892] })

// lazy的模块才能使用i18n常量
// 使用 useMemo 缓存 LayoutWithProps 组件，避免每次渲染都创建新组件
const LayoutWithProps = React.memo((props: any) => <RouteElement fallback={IconLoading} path="*" component={lazy(() => import('@/components/Layout'))} {...props} />)
const Main = <RouteElement fallback={IconLoading} path="*" component={lazy(() => import('@/components/Main'))} />
const Home = <RouteElement path="*" component={lazy(() => import('@/pages/Home'))} />
const ShopChoose = <RouteElement path="*" component={lazy(() => import('@/pages/ShopChoose'))} />
// const NotWindows = <RouteElement path="*" component={lazy(() => import('@/pages/NotWindows'))} />
const isOrderManager = getIsOrderManager()
const isBR = getRegionByCookie() === 'BR'
initOneIdWeb()

const App = () => {
  const { t } = useTranslation()
  const isTop = window.top === window
  const { pathname, hash } = location
  if (pathname === '/' && hash !== '') {
    history.pushState({}, '', hash.slice(1))
  }

  const { currentShopId: shopId } = useShopInfo()

  const { updateShopUserInfo } = useAccountInfo()

  const { getRbacAuthGrayConfig } = useAuthInfo()

  const { updateMessageDotByCategoryList } = useMsgInfo()

  // 心跳上报
  useHeartbeatReport(shopId)

  const isShopInfoFinished = useRecoilValue(shopInfoFinishedAtom)
  const accountDetailInfo = useRecoilValue(accountDetailInfoAtom)
  const [imEntryShow, setImEntryShow] = useRecoilState(imEntryShowAtom)

  const { accountType } = accountDetailInfo

  const isSingle = useMemo(() => {
    if (!accountType) return undefined
    return isSingleAcct(accountType)
  }, [accountType])

  // 当前为单门店模式
  const isSingleShopMode = useMemo(() => shopId && +shopId > 0, [shopId])

  console.log('shopId', shopId, ', isBR', isBR)

  // 使用 useMemo 缓存路由元素，避免不必要的重新渲染
  const orderManagerLayout = useMemo(() => <LayoutWithProps showSide={false} headerType="simple" />, [])

  const defaultLayout = useMemo(() => <LayoutWithProps />, [])

  // 无依赖的可并行接口
  useEffect(() => {
    // 商家账号信息
    updateShopUserInfo()
    // rbac权限灰度开关
    getRbacAuthGrayConfig()
  }, [])

  // 依赖账号信息就绪的
  useEffect(() => {
    // rbac权限资源, 统一都拉取，是否使用rbac会根据灰度开关状态来
    if (accountDetailInfo.acctId) {
      getI18nWebParamsPApi()
    }
  }, [accountDetailInfo])

  // 依赖门店信息(shopid)就绪的
  useEffect(() => {
    // rbac权限资源, 统一都拉取，是否使用rbac会根据灰度开关状态来
    if (isShopInfoFinished) {
      // getRbacAuthResource() 此处只预请求接口，不做数据处理
      // getRbacAuthResourcePApi()// 此处只预请求接口，不做数据处理`
      updateMessageDotByCategoryList()
      if (isBR && isOrderManager && isSingleShopMode) {
        // IM入口，写死: 仅巴西region 和 orderManager展示
        getShowImMsgBoxPApi().then(res => {
          setImEntryShow(res?.showFlag)
        })
      }
    }
  }, [isShopInfoFinished, isSingleShopMode])

  useEffect(() => {
    // const AWP_DEPLOY_ENV = process.env.AWP_DEPLOY_ENV
    // const SWIMLANE = process.env.SWIMLANE
    // const titlePre = AWP_DEPLOY_ENV === 'production' ? '' : `${SWIMLANE || AWPENVMAP[AWP_DEPLOY_ENV] || 'local'}-`
    // @ts-ignore
    const titlePre = process_env_titlePre
    document.title = `${titlePre}${t('bteam_pc_main_page_title', 'Keeta Merchant')}`

    if (getInWindowsApp()) {
      autoUploadLogs()
    }
  }, [])

  // 猎豹日志，进入页面日志，离开页面日志（离开时存到localStorage中，下次进入再报）
  useEffect(() => {
    const timer = setTimeout(() => {
      retryFailedRequests()
      upgradeLog()
      leaveWebSiteLog()
      enterWebSiteLog()
    }, 3000)
    window.addEventListener('beforeunload', saveLeaveWebSiteLog)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  // if (isOrderManager && !isDesktopWindows) {
  //   return <BrowserRouter>
  //     <Routes>
  //       <Route path="*" element={NotWindows} />
  //     </Routes>
  //   </BrowserRouter>
  // }

  return (
    <>
      {isOrderManager && isTop && <CommonNotifacation />}
      <BrowserRouter>
        <Routes>
          {isOrderManager && (!shopId || shopId == -1) && isSingle === false && isBR ? (
            <Route path="*" element={orderManagerLayout}>
              <Route path="*" element={ShopChoose} />
            </Route>
          ) : (
            <>
              <Route path="*" element={defaultLayout}>
                <Route path="*" element={Main} />
              </Route>
              <Route path="*" element={defaultLayout}>
                <Route path="*" element={Home} />
              </Route>
              <Route path="*" element={defaultLayout}>
                <Route path="home" element={Home} />
              </Route>
              <Route path="/web/*" element={Home} />
              <Route path="home" element={Home} />
              <Route path="404" element={Home} />
            </>
          )}
        </Routes>
        {/* </RecoilRoot> */}
      </BrowserRouter>
    </>
  )
}
export default memo(App)
